package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.viewModelScope
import androidx.lifecycle.ViewModel
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlin.time.Clock
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.util.apiBaseUrl
import pl.zar.tikthrow.util.createPostsSettings
import pl.zar.tikthrow.util.currentAuthToken
import pl.zar.tikthrow.util.PlatformPublishResult
import pl.zar.tikthrow.util.publishScheduledPost
import pl.zar.tikthrow.util.scheduledLocalDateTimeAtMillis
import pl.zar.tikthrow.util.scheduleScheduledPostOnServer
import pl.zar.tikthrow.util.scheduleTikTokPostOnServer
import pl.zar.tikthrow.util.userFacingNetworkError

@Serializable
data class ScheduledPost(
    val id: String,
    val type: ContentType,
    val media: List<MediaItem>,
    val title: String = "",
    val caption: String,
    val platforms: Set<Platform>,
    val scheduledDateMillis: Long,
    val scheduledHour: Int,
    val scheduledMinute: Int,
    val youtubeAccountId: String? = null,
    val tiktokAccountId: String? = null,
    val facebookAccountId: String? = null,
    val instagramAccountId: String? = null,
    val serverScheduledPlatforms: Set<Platform> = emptySet(),
    val serverScheduleStatus: ServerScheduleStatus? = null,
    val serverScheduleMessage: String? = null,
    val serverYouTubeScheduledId: String? = null,
    val serverTikTokScheduledId: String? = null,
    val serverScheduleAttempt: Int = 0,
    val serverScheduleUpdatedAtMillis: Long? = null,
)

@Serializable
enum class ServerScheduleStatus {
    PENDING,
    SCHEDULED,
    FAILED
}

@Serializable
enum class PublishedPostStatus {
    PUBLISHED,
    FAILED
}

@Serializable
data class PublishedPost(
    val id: String,
    val sourceScheduledPostId: String,
    val type: ContentType,
    val media: List<MediaItem>,
    val caption: String,
    val platforms: Set<Platform>,
    val scheduledAtMillis: Long,
    val publishedAtMillis: Long,
    val status: PublishedPostStatus,
    val message: String,
)

class ScheduledPostsViewModel : ViewModel() {
    private val settings = runCatching { createPostsSettings() }.getOrNull()
    private val json = Json { ignoreUnknownKeys = true }

    private val _posts = MutableStateFlow<List<ScheduledPost>>(loadScheduledPosts())
    val posts: StateFlow<List<ScheduledPost>> = _posts.asStateFlow()

    private val _publishedPosts = MutableStateFlow<List<PublishedPost>>(loadPublishedPosts())
    val publishedPosts: StateFlow<List<PublishedPost>> = _publishedPosts.asStateFlow()

    init {
        retryPendingServerSchedules()
        startScheduler()
    }

    private fun loadScheduledPosts(): List<ScheduledPost> {
        val raw = settings?.getStringOrNull(KEY) ?: return emptyList()
        return runCatching { json.decodeFromString<List<ScheduledPost>>(raw) }.getOrDefault(emptyList())
    }

    private fun loadPublishedPosts(): List<PublishedPost> {
        val raw = settings?.getStringOrNull(PUBLISHED_KEY) ?: return emptyList()
        return runCatching { json.decodeFromString<List<PublishedPost>>(raw) }.getOrDefault(emptyList())
    }

    private fun persistScheduledPosts(posts: List<ScheduledPost>) {
        settings?.putString(KEY, json.encodeToString(posts))
    }

    /**
     * Keeps only the newest [MAX_PUBLISHED_HISTORY] entries. The history is append-only and the UI
     * never shows more than a handful, so letting it grow unbounded only bloats storage.
     */
    private fun persistPublishedPosts(posts: List<PublishedPost>) {
        val trimmed = posts.sortedByDescending { it.publishedAtMillis }.take(MAX_PUBLISHED_HISTORY)
        settings?.putString(PUBLISHED_KEY, json.encodeToString(trimmed))
    }

    fun save(
        type: ContentType,
        media: List<MediaItem>,
        title: String,
        caption: String,
        platforms: Set<Platform>,
        scheduledDateMillis: Long,
        scheduledHour: Int,
        scheduledMinute: Int,
        youtubeAccountId: String? = null,
        tiktokAccountId: String? = null,
        facebookAccountId: String? = null,
        instagramAccountId: String? = null,
    ): Boolean {
        if (validationError(type, media, caption, platforms, scheduledDateMillis, scheduledHour, scheduledMinute) != null) {
            return false
        }
        val needsServerSchedule = Platform.YOUTUBE in platforms || Platform.TIKTOK in platforms
        val post = ScheduledPost(
            id = Clock.System.now().toEpochMilliseconds().toString(),
            type = type,
            media = media,
            title = title.trim(),
            caption = caption,
            platforms = platforms,
            scheduledDateMillis = scheduledDateMillis,
            scheduledHour = scheduledHour,
            scheduledMinute = scheduledMinute,
            youtubeAccountId = youtubeAccountId,
            tiktokAccountId = tiktokAccountId,
            facebookAccountId = facebookAccountId,
            instagramAccountId = instagramAccountId,
            serverScheduleStatus = if (needsServerSchedule) ServerScheduleStatus.PENDING else null,
            serverScheduleMessage = if (needsServerSchedule) buildServerScheduleMessage("Oczekuje na start uploadu", 0, null, postMediaDebug(media)) else null,
            serverScheduleUpdatedAtMillis = if (needsServerSchedule) Clock.System.now().toEpochMilliseconds() else null,
        )
        _posts.update { updated -> (updated + post).also { persistScheduledPosts(it) } }
        if (Platform.YOUTUBE in platforms) {
            scheduleYouTubeOnServer(post)
        }
        if (Platform.TIKTOK in platforms) {
            scheduleTikTokOnServer(post)
        }
        return true
    }

    /**
     * Publishes a post immediately (as opposed to scheduling it for a later time). The post is
     * pushed through the same scheduler queue with `scheduledAt` set to now, so the publish path
     * (server uploads, status polling, published history) behaves identically to a due scheduled post.
     */
    fun publishNow(
        type: ContentType,
        media: List<MediaItem>,
        title: String,
        caption: String,
        platforms: Set<Platform>,
        youtubeAccountId: String? = null,
        tiktokAccountId: String? = null,
        facebookAccountId: String? = null,
        instagramAccountId: String? = null,
    ): Boolean {
        val now = Clock.System.now().toEpochMilliseconds()
        if (validationError(type, media, caption, platforms, now, 0, 0, allowImmediate = true) != null) {
            return false
        }
        // Push the post with an immediate due time; the scheduler drains it on its next tick.
        // scheduledDateMillis = now with 00:00 guarantees scheduledAtMillis() lands in the past,
        // so publishDuePosts() treats it as already due.
        val post = ScheduledPost(
            id = "now_${now}",
            type = type,
            media = media,
            title = title.trim(),
            caption = caption,
            platforms = platforms,
            scheduledDateMillis = now,
            scheduledHour = 0,
            scheduledMinute = 0,
            youtubeAccountId = youtubeAccountId,
            tiktokAccountId = tiktokAccountId,
            facebookAccountId = facebookAccountId,
            instagramAccountId = instagramAccountId,
        )
        _posts.update { updated -> (updated + post).also { persistScheduledPosts(it) } }
        // Trigger a publish pass right away instead of waiting up to a second for the scheduler.
        viewModelScope.launch { publishDuePosts() }
        return true
    }

    fun update(
        id: String,
        media: List<MediaItem>,
        title: String,
        caption: String,
        platforms: Set<Platform>,
        scheduledDateMillis: Long,
        scheduledHour: Int,
        scheduledMinute: Int,
        youtubeAccountId: String? = null,
        tiktokAccountId: String? = null,
        facebookAccountId: String? = null,
        instagramAccountId: String? = null,
    ): Boolean {
        val existing = getById(id) ?: return false
        if (validationError(existing.type, media, caption, platforms, scheduledDateMillis, scheduledHour, scheduledMinute) != null) {
            return false
        }
        val needsServerSchedule = Platform.YOUTUBE in platforms || Platform.TIKTOK in platforms
        _posts.update { list ->
            list.map {
                if (it.id == id) it.copy(
                    media = media,
                    title = title.trim(),
                    caption = caption,
                    platforms = platforms,
                    scheduledDateMillis = scheduledDateMillis,
                    scheduledHour = scheduledHour,
                    scheduledMinute = scheduledMinute,
                    youtubeAccountId = youtubeAccountId,
                    tiktokAccountId = tiktokAccountId,
                    facebookAccountId = facebookAccountId,
                    instagramAccountId = instagramAccountId,
                    serverScheduledPlatforms = emptySet(),
                    serverScheduleStatus = if (needsServerSchedule) ServerScheduleStatus.PENDING else null,
                    serverScheduleMessage = if (needsServerSchedule) buildServerScheduleMessage("Oczekuje na start uploadu", 0, null, postMediaDebug(media)) else null,
                    serverYouTubeScheduledId = null,
                    serverTikTokScheduledId = null,
                    serverScheduleAttempt = 0,
                    serverScheduleUpdatedAtMillis = if (needsServerSchedule) Clock.System.now().toEpochMilliseconds() else null,
                ) else it
            }.also { persistScheduledPosts(it) }
        }
        getById(id)?.let { post ->
            if (Platform.YOUTUBE in post.platforms) scheduleYouTubeOnServer(post)
            if (Platform.TIKTOK in post.platforms) scheduleTikTokOnServer(post)
        }
        return true
    }

    fun delete(id: String) {
        _posts.update { it.filter { p -> p.id != id }.also { persistScheduledPosts(it) } }
    }

    fun getById(id: String): ScheduledPost? = _posts.value.find { it.id == id }

    fun retryYouTubeServerSchedule(id: String) {
        val post = getById(id) ?: return
        if (Platform.YOUTUBE !in post.platforms) return

        _posts.update { current ->
            current.map {
                if (it.id == id) {
                    it.copy(
                        serverScheduledPlatforms = it.serverScheduledPlatforms - Platform.YOUTUBE,
                        serverScheduleStatus = ServerScheduleStatus.PENDING,
                        serverScheduleMessage = buildServerScheduleMessage(
                            stage = "Reczna ponowna proba zlecona",
                            attempt = it.serverScheduleAttempt + 1,
                            serverId = null,
                            details = postMediaDebug(it.media),
                        ),
                        serverYouTubeScheduledId = null,
                        serverScheduleAttempt = it.serverScheduleAttempt + 1,
                        serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                    )
                } else {
                    it
                }
            }.also { persistScheduledPosts(it) }
        }

        getById(id)?.let { scheduleYouTubeOnServer(it, preserveAttempt = true) }
    }

    private fun scheduleYouTubeOnServer(post: ScheduledPost, preserveAttempt: Boolean = false) {
        viewModelScope.launch {
            val attempt = if (preserveAttempt) post.serverScheduleAttempt.coerceAtLeast(1)
                          else post.serverScheduleAttempt + 1
            updateServerScheduleDebug(
                postId = post.id,
                status = ServerScheduleStatus.PENDING,
                message = buildServerScheduleMessage("Start YouTube upload do backendu", attempt, null, postMediaDebug(post.media)),
                attempt = attempt,
                youtubeServerId = null,
                tiktokServerId = null,
                markYouTubeScheduled = false,
                markTikTokScheduled = false,
            )
            val result = runCatching {
                withTimeout(SERVER_UPLOAD_TIMEOUT_MILLIS) { scheduleScheduledPostOnServer(post) }
            }.getOrElse { exception ->
                PlatformPublishResult(false, buildServerScheduleMessage("YouTube upload przerwany", attempt, null, userFacingNetworkError("Upload do backendu", exception)))
            }
            _posts.update { current ->
                current.map {
                    if (it.id != post.id) return@map it
                    if (result.success) {
                        it.copy(
                            serverScheduledPlatforms = it.serverScheduledPlatforms + Platform.YOUTUBE,
                            serverScheduleStatus = if (Platform.TIKTOK !in it.platforms || it.serverTikTokScheduledId != null) ServerScheduleStatus.SCHEDULED else it.serverScheduleStatus,
                            serverScheduleMessage = buildServerScheduleMessage("YouTube backend przyjal harmonogram", attempt, result.serverId, result.message),
                            serverYouTubeScheduledId = result.serverId,
                            serverScheduleAttempt = attempt,
                            serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                        )
                    } else {
                        it.copy(
                            serverScheduleStatus = ServerScheduleStatus.FAILED,
                            serverScheduleMessage = buildServerScheduleMessage("YouTube backend odrzucil harmonogram", attempt, result.serverId, result.message),
                            serverScheduleAttempt = attempt,
                            serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                        )
                    }
                }.also { persistScheduledPosts(it) }
            }
        }
    }

    private fun scheduleTikTokOnServer(post: ScheduledPost, preserveAttempt: Boolean = false) {
        viewModelScope.launch {
            val attempt = if (preserveAttempt) post.serverScheduleAttempt.coerceAtLeast(1)
                          else post.serverScheduleAttempt + 1
            val result = runCatching {
                withTimeout(SERVER_UPLOAD_TIMEOUT_MILLIS) { scheduleTikTokPostOnServer(post) }
            }.getOrElse { exception ->
                PlatformPublishResult(false, userFacingNetworkError("Upload TikTok do backendu", exception))
            }
            _posts.update { current ->
                current.map {
                    if (it.id != post.id) return@map it
                    if (result.success) {
                        val allScheduled = Platform.YOUTUBE !in it.platforms || Platform.YOUTUBE in it.serverScheduledPlatforms
                        it.copy(
                            serverScheduledPlatforms = it.serverScheduledPlatforms + Platform.TIKTOK,
                            serverScheduleStatus = if (allScheduled) ServerScheduleStatus.SCHEDULED else it.serverScheduleStatus,
                            serverScheduleMessage = buildServerScheduleMessage("TikTok backend przyjal harmonogram", attempt, result.serverId, result.message),
                            serverTikTokScheduledId = result.serverId,
                            serverScheduleAttempt = attempt,
                            serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                        )
                    } else {
                        it.copy(
                            serverScheduleStatus = ServerScheduleStatus.FAILED,
                            serverScheduleMessage = buildServerScheduleMessage("TikTok backend odrzucil harmonogram", attempt, result.serverId, result.message),
                            serverScheduleAttempt = attempt,
                            serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                        )
                    }
                }.also { persistScheduledPosts(it) }
            }
        }
    }

    private fun updateServerScheduleDebug(
        postId: String,
        status: ServerScheduleStatus,
        message: String,
        attempt: Int,
        youtubeServerId: String?,
        tiktokServerId: String?,
        markYouTubeScheduled: Boolean,
        markTikTokScheduled: Boolean,
    ) {
        _posts.update { current ->
            current.map {
                if (it.id == postId) {
                    val updatedPlatforms = it.serverScheduledPlatforms
                        .let { set -> if (markYouTubeScheduled) set + Platform.YOUTUBE else set - Platform.YOUTUBE }
                        .let { set -> if (markTikTokScheduled) set + Platform.TIKTOK else set - Platform.TIKTOK }
                    it.copy(
                        serverScheduledPlatforms = updatedPlatforms,
                        serverScheduleStatus = status,
                        serverScheduleMessage = message,
                        serverYouTubeScheduledId = youtubeServerId ?: it.serverYouTubeScheduledId,
                        serverTikTokScheduledId = tiktokServerId ?: it.serverTikTokScheduledId,
                        serverScheduleAttempt = attempt,
                        serverScheduleUpdatedAtMillis = Clock.System.now().toEpochMilliseconds(),
                    )
                } else {
                    it
                }
            }.also { persistScheduledPosts(it) }
        }
    }

    private fun retryPendingServerSchedules() {
        _posts.value.forEach { post ->
            if (Platform.YOUTUBE in post.platforms &&
                post.serverScheduleStatus == ServerScheduleStatus.PENDING &&
                post.serverYouTubeScheduledId == null
            ) {
                scheduleYouTubeOnServer(post)
            }
            if (Platform.TIKTOK in post.platforms &&
                post.serverScheduleStatus != ServerScheduleStatus.SCHEDULED &&
                post.serverTikTokScheduledId == null
            ) {
                scheduleTikTokOnServer(post)
            }
        }
    }

    fun scheduledAtMillis(dateMillis: Long, hour: Int, minute: Int): Long {
        return scheduledLocalDateTimeAtMillis(dateMillis, hour, minute)
    }

    fun isFutureSchedule(dateMillis: Long, hour: Int, minute: Int): Boolean {
        return scheduledAtMillis(dateMillis, hour, minute) > Clock.System.now().toEpochMilliseconds()
    }

    fun validationError(
        type: ContentType,
        media: List<MediaItem>,
        caption: String,
        platforms: Set<Platform>,
        scheduledDateMillis: Long?,
        scheduledHour: Int,
        scheduledMinute: Int,
        allowImmediate: Boolean = false,
    ): String? {
        if (media.isEmpty() && caption.isBlank()) return "Dodaj opis albo media"
        if (platforms.isEmpty()) return "Wybierz co najmniej jedna platforme"
        if (!allowImmediate &&
            (scheduledDateMillis == null || !isFutureSchedule(scheduledDateMillis, scheduledHour, scheduledMinute))
        ) {
            return "Data publikacji musi byc w przyszlosci"
        }
        if (type == ContentType.POST && media.any { it.isVideo }) {
            return "Post moze zawierac tekst i zdjecia, bez wideo"
        }
        if (type == ContentType.POST && Platform.YOUTUBE in platforms) {
            return "YouTube obsluguje tutaj tylko Wideo albo Shorts"
        }
        if (type != ContentType.POST && media.none { it.isVideo }) {
            return "${type.label} wymaga pliku wideo"
        }
        val oversized = media.firstOrNull { (it.fileSizeBytes ?: 0L) > MAX_UPLOAD_SIZE_BYTES }
        if (oversized != null) {
            val sizeMB = (oversized.fileSizeBytes ?: 0L) / (1024L * 1024L)
            val name = oversized.fileName ?: "plik"
            return "\"$name\" ($sizeMB MB) przekracza limit ${MAX_UPLOAD_SIZE_BYTES / (1024L * 1024L * 1024L)} GB"
        }
        return null
    }

    private fun startScheduler() {
        viewModelScope.launch {
            while (true) {
                val delayMillis = publishDuePosts()
                delay(delayMillis)
            }
        }
    }

    private suspend fun publishDuePosts(): Long {
        val now = Clock.System.now().toEpochMilliseconds()
        val currentPosts = _posts.value
        val duePosts = currentPosts.filter { it.scheduledAtMillis() <= now }

        if (duePosts.isNotEmpty()) {
            val resolved = duePosts.mapNotNull { post -> post.publish(now) }
            if (resolved.isNotEmpty()) {
                _publishedPosts.update { current ->
                    (resolved + current)
                        .distinctBy { it.sourceScheduledPostId }
                        .also { persistPublishedPosts(it) }
                }
                _posts.update { current ->
                    current
                        .filterNot { post -> resolved.any { it.sourceScheduledPostId == post.id } }
                        .also { persistScheduledPosts(it) }
                }
            }
        }

        val nextPostMillis = _posts.value.minOfOrNull { it.scheduledAtMillis() }
        return when {
            nextPostMillis == null -> DEFAULT_SCHEDULER_DELAY_MILLIS
            nextPostMillis <= now -> MIN_SCHEDULER_DELAY_MILLIS
            else -> (nextPostMillis - now).coerceIn(MIN_SCHEDULER_DELAY_MILLIS, DEFAULT_SCHEDULER_DELAY_MILLIS)
        }
    }

    private fun ScheduledPost.scheduledAtMillis(): Long {
        return scheduledAtMillis(scheduledDateMillis, scheduledHour, scheduledMinute)
    }

    /** Returns null when the outcome is still unresolved (e.g. server-side status check timed out) — the caller should leave the post in the queue and retry later instead of guessing success. */
    private suspend fun ScheduledPost.publish(nowMillis: Long): PublishedPost? {
        val results = mutableListOf<PlatformPublishResult>()

        // Platforms the server already accepted as scheduled (YouTube/TikTok uploads handed over at
        // save time) just need their status checked; everything else is published from here.
        if (Platform.YOUTUBE in platforms && Platform.YOUTUBE in serverScheduledPlatforms) {
            val ytResult = if (serverYouTubeScheduledId != null) {
                checkServerYouTubeStatus(serverYouTubeScheduledId) ?: return null
            } else {
                PlatformPublishResult(true, "YouTube zaplanowany po stronie serwera")
            }
            results.add(ytResult)
        }

        if (Platform.TIKTOK in platforms && Platform.TIKTOK in serverScheduledPlatforms) {
            val ttResult = if (serverTikTokScheduledId != null) {
                checkServerTikTokStatus(serverTikTokScheduledId) ?: return null
            } else {
                PlatformPublishResult(true, "TikTok zaplanowany po stronie serwera")
            }
            results.add(ttResult)
        }

        // Anything not handed to the server scheduler — including every FB/IG/Discord/Twitter
        // selection and any YouTube/TikTok that publish immediately — goes out through the client.
        val remainingPlatforms = platforms - serverScheduledPlatforms
        if (remainingPlatforms.isNotEmpty()) {
            results.add(publishScheduledPost(this.copy(platforms = remainingPlatforms)))
        }

        val success = results.all { it.success }
        val message = results.joinToString(" | ") { it.message }

        return PublishedPost(
            id = "published_${id}_$nowMillis",
            sourceScheduledPostId = id,
            type = type,
            media = media,
            caption = caption,
            platforms = platforms,
            scheduledAtMillis = scheduledAtMillis(),
            publishedAtMillis = nowMillis,
            status = if (success) PublishedPostStatus.PUBLISHED else PublishedPostStatus.FAILED,
            message = message,
        )
    }

    private val httpClient = HttpClient {
        install(ContentNegotiation) { json(Json { ignoreUnknownKeys = true }) }
    }

    @Serializable
    private data class ServerScheduleStatusResponse(
        val status: String,
        val youtubeVideoId: String = "",
        val errorMessage: String = "",
        val scheduledAt: String = "",
    )

    /** Returns null if the post is still PENDING/PUBLISHING after the poll window — caller should retry later rather than assume success. */
    private suspend fun checkServerYouTubeStatus(scheduleId: String): PlatformPublishResult? {
        val token = currentAuthToken()
            ?: return PlatformPublishResult(false, "Brak tokenu autoryzacji do sprawdzenia statusu YouTube")

        println("[SCHEDULER_CLIENT] sprawdzam status scheduleId=$scheduleId")

        repeat(12) { attempt ->
            runCatching {
                val response = httpClient.get("$apiBaseUrl/api/publish/youtube/schedule/$scheduleId/status") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                if (response.status.isSuccess()) {
                    val body = response.body<ServerScheduleStatusResponse>()
                    println("[SCHEDULER_CLIENT] attempt=$attempt status=${body.status}, videoId=${body.youtubeVideoId}, error=${body.errorMessage}")
                    when (body.status) {
                        "PUBLISHED" -> return PlatformPublishResult(
                            true,
                            "YouTube opublikowany${if (body.youtubeVideoId.isNotBlank()) ": https://youtu.be/${body.youtubeVideoId}" else ""}",
                        )
                        "FAILED" -> return PlatformPublishResult(
                            false,
                            "YouTube błąd publikacji: ${body.errorMessage.ifBlank { "nieznany błąd" }}",
                        )
                        else -> { /* PENDING lub PUBLISHING — czekamy */ }
                    }
                } else {
                    println("[SCHEDULER_CLIENT] HTTP ${response.status.value} dla scheduleId=$scheduleId")
                }
            }.onFailure { e ->
                println("[SCHEDULER_CLIENT] ${userFacingNetworkError("Sprawdzenie statusu YouTube", e)}")
            }
            delay(5_000L)
        }
        return null
    }

    @Serializable
    private data class ServerTikTokStatusResponse(
        val status: String,
        val tikTokPublishId: String = "",
        val errorMessage: String = "",
        val scheduledAt: String = "",
    )

    /** Returns null if the post is still PENDING/PUBLISHING after the poll window — caller should retry later rather than assume success. */
    private suspend fun checkServerTikTokStatus(scheduleId: String): PlatformPublishResult? {
        val token = currentAuthToken()
            ?: return PlatformPublishResult(false, "Brak tokenu autoryzacji do sprawdzenia statusu TikTok")

        println("[SCHEDULER_CLIENT] sprawdzam status TikTok scheduleId=$scheduleId")

        repeat(12) { attempt ->
            runCatching {
                val response = httpClient.get("$apiBaseUrl/api/publish/tiktok/schedule/$scheduleId/status") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                if (response.status.isSuccess()) {
                    val body = response.body<ServerTikTokStatusResponse>()
                    println("[SCHEDULER_CLIENT] TikTok attempt=$attempt status=${body.status}, error=${body.errorMessage}")
                    when (body.status) {
                        "PUBLISHED" -> return PlatformPublishResult(true, "TikTok opublikowany")
                        "FAILED" -> return PlatformPublishResult(
                            false,
                            "TikTok błąd publikacji: ${body.errorMessage.ifBlank { "nieznany błąd" }}",
                        )
                        else -> { /* PENDING lub PUBLISHING — czekamy */ }
                    }
                } else {
                    println("[SCHEDULER_CLIENT] TikTok HTTP ${response.status.value} dla scheduleId=$scheduleId")
                }
            }.onFailure { e ->
                println("[SCHEDULER_CLIENT] ${userFacingNetworkError("Sprawdzenie statusu TikTok", e)}")
            }
            delay(5_000L)
        }
        return null
    }

    companion object {
        private const val KEY = "scheduled_posts"
        private const val PUBLISHED_KEY = "published_posts"
        private const val MAX_PUBLISHED_HISTORY = 50
        private const val MIN_SCHEDULER_DELAY_MILLIS = 1_000L
        private const val DEFAULT_SCHEDULER_DELAY_MILLIS = 1_000L
        private const val SERVER_UPLOAD_TIMEOUT_MILLIS = 30 * 60 * 1000L
        const val MAX_UPLOAD_SIZE_BYTES = 2L * 1024L * 1024L * 1024L // 2 GB
    }
}

private fun buildServerScheduleMessage(
    stage: String,
    attempt: Int,
    serverId: String?,
    details: String?,
): String {
    return listOfNotNull(
        stage,
        "proba=${attempt.coerceAtLeast(1)}",
        serverId?.takeIf { it.isNotBlank() }?.let { "id=$it" },
        details?.takeIf { it.isNotBlank() },
    ).joinToString(" | ")
}

private fun postMediaDebug(media: List<MediaItem>): String {
    val first = media.firstOrNull()
    return if (first == null) {
        "media=brak"
    } else {
        "media=${media.size}, video=${media.count { it.isVideo }}, mime=${first.mimeType ?: "?"}, file=${first.fileName ?: "?"}, uri=${first.uri.take(100)}"
    }
}
